<template>
  <aw-dropdown
    class="dropdown relative bg-white InputGroupFocus"
    placement="bottom-start"
    @show="onDropdownShow"
    @hide="onDropdownHide"
    v-model="opened"
    v-bind="$attrs"
  >
    <template v-slot:btn>
      <div
        @click="openSelect"
        class="relative text-left cursor-default dropdown-button"
        :class="!showExpansion && contractPill && enableExpansion ? 'pill-Contracted' : 'pill-Expanded w-40'"
      >
        <template v-if="!multiple">
          <input
            type="text"
            v-model="searchInput.value"
            :placeholder="searchInput.placeholder"
            class="
              nomultiple
              p-0
              outline-none
              ring-0
              block
              w-full
              text-sm
              placeholder-gray-600
              form-control
            "
            ref="search"
            @focus="openSelect"
          />
          <span
            class="
              BcSelect__Icon
              absolute
              inset-y-0
              right-0
              flex
              items-center
              pr-2
              pointer-events-none
              text-gray-400
            "
          >
            <aw-icon 
              v-if="!showExpansion && contractPill && enableExpansion"
              icon="user-add" 
              class="w-5 h-5"
            />
            <!-- Heroicon name: selector -->
            <svg
              v-else
              class="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
        </template>
        <template v-else>
          <div class="BcSelectMultiTags">
            <template v-for="option in selectedOptions" :key="option.id">
              <slot
                name="multitagselected"
                :option="option"
                v-bind="{ removeOption }"
              >
                <div
                  class="
                    bg-bc-blue
                    text-white
                    inline-flex
                    items-center
                    text-sm
                    rounded
                    mr-1
                    overflow-hidden
                  "
                >
                  <span class="ml-2 mr-1 leading-snug truncate max-w-xs p-0"
                    >{{ option.label }}
                  </span>
                  <button
                    class="
                      text-white
                      bg-bc-blue
                      focus:outline-none
                      remove
                      inline-flex
                      items-center
                    "
                    @click.prevent.stop="removeOption(option)"
                    tabindex="-1"
                    title="Remove"
                  >
                    <svg
                      class="w-6 h-6  mx-auto"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M15.78 14.36a1 1 0 0 1-1.42 1.42l-2.82-2.83-2.83 2.83a1 1 0 1 1-1.42-1.42l2.83-2.82L7.3 8.7a1 1 0 0 1 1.42-1.42l2.83 2.83 2.82-2.83a1 1 0 0 1 1.42 1.42l-2.83 2.83 2.83 2.82z"
                      />
                    </svg>
                  </button>
                </div>
              </slot>
            </template>

            <input
              type="text"
              v-model="searchInput.value"
              :placeholder="searchInput.placeholder"
              class="
                multiple
                p-0
                border-none
                inline-flex
                outline-none
                ring-0
                text-sm
                placeholder-gray-600
                focus:outline-none focus:ring-0
              "
              ref="search"
              @focus="openSelect"
            />
          </div>
        </template>
      </div>
    </template>

    <template v-slot:dropdown="{ opened, dropdown }">
      <ul
        tabindex="-1"
        role="listbox"
        class="
          dropdown-menu
          Dropdown Dropdown--Select
          max-w-full
          bg-white
          shadow-md
          rounded-md
          z-10
          text-sm
          overflow-hidden
        "
        v-show="opened"
        :ref="dropdown"
      >
        <!-- No results -->
        <slot name="noresults" v-bind="{ searchInput }" v-if="showNoResults">
          <li class="dropdown-item px-4 py-2 text-red-500">
            {{$t('No result found, change search term')}}
          </li>
        </slot>

        <!--  Max items exceeded -->
        <slot
          name="maxresults"
          v-bind="{ searchInput, maxOptions, filteredOptions }"
          v-if="showMaxResultsExceeded"
        >
          <li class="dropdown-item px-4 py-2 text-bc-blue">
<!--            todo check-->
            {{$t('Showing the first %maxOptions% records from %length%. Specify your search term', {maxOptions: maxOptions, length: filteredOptions.length})}}
          </li>
        </slot>

        <!-- List of available items -->
        <template
          v-for="(option, optionIndex) in filteredAndLimitedOptions"
          :key="option.id"
        >
          <slot
            name="option"
            v-bind="{
              option,
              handleMouseEnter,
              selectOption,
              highlighted: optionIndex === selectedIndex,
              selected: value.indexOf(option.id) !== -1
            }"
          >
            <li
              role="option"
              class="
                cursor-default
                select-none
                relative
                py-2
                pl-4
                pr-4
                truncate
                d-flex
              "
              :class="{
                'text-white bg-bc-blue': optionIndex === selectedIndex,
                'text-gray-800': optionIndex !== selectedIndex
              }"
              @mouseenter="handleMouseEnter(option)"
              @click.stop="selectOption(option)"
            >
              {{ option.label }}
            </li>
          </slot>
        </template>
      </ul>
    </template>
  </aw-dropdown>

  <select
    v-if="!multiple"
    :name="name"
    :data-nette-rules="dataNetteRules"
    :data-lfv-message-id="id + '_message'"
    v-model="selectVal"
    class="hidden"
    ref="select"
  >
    <option v-for="item in options" :key="item.id" :value="item.id">
      {{ item.label }}
    </option>
  </select>

  <select
    v-if="multiple"
    :name="name"
    multiple="multiple"
    :data-nette-rules="dataNetteRules"
    :data-lfv-message-id="id + '_message'"
    v-model="selectVal"
    class="hidden"
    ref="select"
  >
    <option v-for="item in options" :key="item.id" :value="item.id">
      {{ item.label }}
    </option>
  </select>
</template>
<script>
import { search } from 'ss-search'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
// import { toRaw } from 'vue'
import clone from 'lodash/clone'
import isEqual from 'lodash/isEqual'
import i18next from "i18next";

export default {
  emits: ['update:modelValue', 'update:searchValue'],
  props: {
    modelValue: {
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    enableExpansion: {
      type: Boolean,
      default: false
    },
    contractPill: {
      type: Boolean,
      default: false
    },
    searchInOptions: {
      type: [Array, Boolean],
      default: () => ['label']
    },
    defaultPlaceholder: {
      type: String,
      default: i18next.t('Vyberte')
    },
    maxOptions: {
      type: Number,
      default: 50
    },
    name: String,
    id: String,
    dataNetteRules: String,
    onSelectOption: {
      type: Function,
      default: function (option, selectOption) {
        selectOption(option)
      }
    }
  },
  data() {
    let placeholder = this.defaultPlaceholder
    let selectedOption = null
    if (
      this.multiple === false &&
      (selectedOption = find(this.options, ['id', this.modelValue]))
    ) {
      placeholder = selectedOption.label
    } else if (this.multiple && this.modelValue.length > 0) {
      placeholder = ''
    }
    return {
      selectedIndex: 0,
      searchInput: {
        placeholder,
        value: ''
      },
      opened: false,
      value:
        this.multiple === false
          ? [clone(this.modelValue)]
          : clone(this.modelValue),
      lastVal: clone(this.modelValue),
      showExpansion: false,
    }
  },
  computed: {
    filteredOptions() {
      const options =
        this.multiple === false
          ? this.options
          : this.options.filter((a) => this.value.indexOf(a.id) === -1)

      if (this.searchInput.value == '') {
        return options
      }
      return this.searchInOptions
        ? search(options, this.searchInOptions, this.searchInput.value)
        : options
    },
    selectedOptions() {
      return this.value.map((itemId) => find(this.options, ['id', itemId]))
    },
    showNoResults() {
      return this.filteredOptions.length === 0
    },
    showMaxResultsExceeded() {
      return this.filteredOptions.length > this.maxOptions
    },
    filteredAndLimitedOptions() {
      return this.filteredOptions.slice(0, this.maxOptions)
    },
    selectVal: {
      get() {
        return this.modelValue
      },
      set(val) {
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          if (!isEqual(this.lastVal, val)) {
            setTimeout(() => {
              this.$refs.select.dispatchEvent(
                new Event('change', { bubbles: true })
              )
            }, 50)
            this.lastVal = clone(val)
          }
        }, 50)
      }
    }
  },
  methods: {
    addKeyHandler() {
      window.addEventListener('keydown', this.keyHandler)
    },
    removeKeyHandler() {
      window.removeEventListener('keydown', this.keyHandler)
    },
    keyHandler(e) {
      /**
        38 — up
        40 — down
         9 — tab
        13 — enter
        27 — escape
         8 - backspace
      **/
      const key = e.which || e.keyCode
      if (key === 38 /*|| (e.shiftKey && key === 9)*/) {
        this.handleKeyUp(e)
      } else if (key === 40 /*|| key === 9*/) {
        this.handleKeyDown(e)
      } else if (key === 13) {
        this.handleEnter(e)
      } else if (key === 27) {
        //this.handleEnter(e);
        e.preventDefault()
        this.closeSelect()
      } else if (key === 8) {
        //this.handleEnter(e);
        if (this.value.length && this.searchInput.value.length === 0) {
          e.preventDefault()
          this.value.pop()
          this.$emit('update:modelValue', this.value)
          this.refresh()
        }
      }
    },
    handleEnter(e) {
      e.preventDefault()
      if (this.filteredOptions.length && this.filteredOptions[this.selectedIndex]) {
        this.onSelectOption.call(
          this,
          this.filteredOptions[this.selectedIndex],
          this.doSelectOption
        )
      }
    },
    handleKeyUp(e) {
      e.preventDefault()
      if (this.selectedIndex <= 0) {
        // If index is less than or equal to zero then set it to the last item index
        this.selectedIndex = this.filteredOptions.length - 1
      } else if (
        this.selectedIndex > 0 &&
        this.selectedIndex <= this.filteredOptions.length - 1
      ) {
        // If index is larger than zero and smaller or equal to last index then decrement
        this.selectedIndex--
      }
    },
    handleKeyDown(e) {
      e.preventDefault()
      // Check if index is below 0
      // This means that we did not start yet
      if (
        this.selectedIndex < 0 ||
        this.selectedIndex === this.filteredOptions.length - 1
      ) {
        // Set the index to the first item
        this.selectedIndex = 0
      } else if (
        this.selectedIndex >= 0 &&
        this.selectedIndex < this.filteredOptions.length - 1
      ) {
        this.selectedIndex++
      }
    },
    handleMouseEnter(option) {
      this.selectedIndex = findIndex(this.filteredOptions, ['id', option.id])
    },
    selectOption(option) {
      this.onSelectOption.call(this, option, this.doSelectOption)
    },
    doSelectOption(option) {
      if (!this.multiple) {
        this.searchInput.placeholder = option.label
        this.searchInput.value = ''

        this.value = [option.id]
        this.$emit('update:modelValue', this.value[0])

        this.$refs.search.blur()
        this.opened = false
      } else if (this.value.indexOf(option.id) === -1) {
        this.searchInput.placeholder = ''
        this.value.push(option.id)
        this.$emit('update:modelValue', this.value)

        // kvuli prekryvu dropdownu a inputu s naseptavanim
        this.refresh()

        // vycisteni inputu od jeho posledni hodnoty
        this.searchInput.value = ''
      } else {
        this.removeOption(option)
        if (this.value.length === 0) {
          this.searchInput.placeholder = this.defaultPlaceholder
        }
      }
    },

    refresh() {
      if (this.opened) {
        this.opened = false
        this.$nextTick(() => {
          this.opened = true
        })
      }

      let placeholder = this.defaultPlaceholder
      let selectedOption = null
      if (
        this.multiple === false &&
        (selectedOption = find(this.options, ['id', this.modelValue]))
      ) {
        placeholder = selectedOption.label
      } else if (this.multiple && this.modelValue.length > 0) {
        placeholder = ''
      }

      this.searchInput.placeholder = placeholder
    },

    removeOption(option) {
      this.value.splice(this.value.indexOf(option.id), 1)
      this.$emit('update:modelValue', this.value)

      // kvuli prekryvu dropdownu a inputu s naseptavanim
      this.refresh()
    },
    closeSelect() {
      this.searchInput.value = ''
      this.$refs.search.blur()
      this.opened = false
      this.showExpansion = false
    },
    openSelect() {
      this.$refs.search.focus()
      this.opened = true
      this.showExpansion = true
    },
    onDropdownShow() {
      this.addKeyHandler()
      this.$refs.search.focus()
    },
    onDropdownHide() {
      this.removeKeyHandler()
      this.searchInput.value = ''
      this.$refs.search?.blur()
      this.showExpansion = false
    }
  },
  watch: {
    'searchInput.value'(val) {
      this.selectedIndex = 0
      this.$emit('update:searchValue', val)
    },
    modelValue(val) {
      const clonedVal = clone(val)
      this.value = this.multiple === false ? [clonedVal] : clonedVal
      this.selectVal = clonedVal
      this.refresh()
    }
  }
}
</script>
